import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Shield, CheckCircle2, AlertTriangle, MapPin, User, Mail, KeyRound, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlarmasRegistroPublicoPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [estado, setEstado] = useState('validando'); // validando | valido | invalido | enviado
    const [empresa, setEmpresa] = useState('');
    const [customIcon, setCustomIcon] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({ nombre: '', direccion: '', email: '', password: '', confirm: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        validate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const validate = async () => {
        try {
            const res = await fetch(`${API_URL}/api/alarmas/registro/${token}/validar`);
            if (res.ok) {
                const data = await res.json();
                setEmpresa(data.empresa_nombre || 'Alarmas');
                setCustomIcon(!!data.custom_icon);
                setEstado('valido');
            } else {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Link no válido');
                setEstado('invalido');
            }
        } catch {
            setError('Error de conexión');
            setEstado('invalido');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.direccion || !form.email || !form.password) {
            toast.error('Complete todos los campos');
            return;
        }
        if (form.password !== form.confirm) {
            toast.error('Las contraseñas no coinciden');
            return;
        }
        if (form.password.length < 6) {
            toast.error('Contraseña mínimo 6 caracteres');
            return;
        }
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/alarmas/registro/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombre: form.nombre.trim(),
                    direccion: form.direccion.trim(),
                    email: form.email.trim().toLowerCase(),
                    password: form.password,
                }),
            });
            if (res.ok) {
                setEstado('enviado');
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al enviar');
            }
        } catch {
            toast.error('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const logoSrc = customIcon ? '/icons/custom/icon-192.png' : '/icons/alarmas-192.png';

    if (estado === 'validando') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4">
                <Skeleton className="h-96 w-full max-w-md" />
            </div>
        );
    }

    if (estado === 'invalido') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-red-50 to-orange-50 dark:from-zinc-950 dark:to-red-950">
                <Card className="max-w-md w-full border-2 border-red-500/30">
                    <CardHeader className="text-center">
                        <AlertTriangle className="h-16 w-16 mx-auto text-red-500 mb-3" />
                        <CardTitle className="text-2xl">Link no válido</CardTitle>
                        <CardDescription className="text-base">{error}</CardDescription>
                    </CardHeader>
                    <CardContent className="text-center">
                        <p className="text-muted-foreground text-sm">Contacta al administrador para obtener un link nuevo.</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (estado === 'enviado') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-zinc-950 dark:to-emerald-950">
                <Card className="max-w-md w-full border-2 border-emerald-500/30">
                    <CardHeader className="text-center">
                        <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-3" />
                        <CardTitle className="text-2xl">¡Solicitud enviada!</CardTitle>
                        <CardDescription className="text-base mt-2">
                            El administrador de <strong>{empresa}</strong> revisará tu solicitud.
                            Recibirás acceso una vez aprobada.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center space-y-3">
                        <p className="text-muted-foreground text-sm">
                            Podrás ingresar con tu correo y contraseña una vez el administrador apruebe tu cuenta.
                        </p>
                        <Button onClick={() => navigate('/login')} data-testid="btn-ir-login" className="w-full">
                            Ir al ingreso
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-red-50 to-orange-50 dark:from-zinc-950 dark:to-red-950">
            <Card className="max-w-md w-full border-2 shadow-xl">
                <CardHeader className="text-center">
                    <img src={logoSrc} alt="Logo" className="w-20 h-20 mx-auto rounded-2xl border-2 mb-2" />
                    <CardTitle className="flex items-center justify-center gap-2 text-2xl">
                        <Shield className="h-6 w-6 text-red-500" />
                        {empresa}
                    </CardTitle>
                    <CardDescription className="text-base">
                        Regístrate para controlar las alarmas de tu comunidad
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-base">
                                <User className="h-4 w-4" /> Nombre completo
                            </Label>
                            <Input
                                value={form.nombre}
                                onChange={e => setForm({ ...form, nombre: e.target.value })}
                                placeholder="Carlos Perez"
                                className="h-12 text-base"
                                data-testid="reg-nombre"
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-base">
                                <MapPin className="h-4 w-4" /> Calle y número de casa
                            </Label>
                            <Input
                                value={form.direccion}
                                onChange={e => setForm({ ...form, direccion: e.target.value })}
                                placeholder="Murillo 1467"
                                className="h-12 text-base"
                                data-testid="reg-direccion"
                                required
                            />
                            <p className="text-xs text-muted-foreground">
                                Se mostrará en las notificaciones que envíes (ej: "Carlos de Murillo 1467 activó la sirena").
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-base">
                                <Mail className="h-4 w-4" /> Correo electrónico
                            </Label>
                            <Input
                                type="email"
                                value={form.email}
                                onChange={e => setForm({ ...form, email: e.target.value })}
                                placeholder="tu@correo.com"
                                className="h-12 text-base"
                                data-testid="reg-email"
                                required
                            />
                            <p className="text-xs text-muted-foreground">Este será tu <strong>nombre de usuario</strong> para ingresar.</p>
                        </div>
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-base">
                                <KeyRound className="h-4 w-4" /> Contraseña
                            </Label>
                            <Input
                                type="password"
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                placeholder="Mínimo 6 caracteres"
                                className="h-12 text-base"
                                data-testid="reg-password"
                                required
                            />
                            <p className="text-xs text-muted-foreground">Esta será tu <strong>clave secreta</strong> para ingresar. No la compartas.</p>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-base">Confirma tu contraseña</Label>
                            <Input
                                type="password"
                                value={form.confirm}
                                onChange={e => setForm({ ...form, confirm: e.target.value })}
                                placeholder="Escríbela de nuevo"
                                className="h-12 text-base"
                                data-testid="reg-confirm"
                                required
                            />
                        </div>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-12 text-base font-bold bg-red-500 hover:bg-red-600"
                            data-testid="btn-registrar"
                        >
                            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enviando...</> : 'Enviar solicitud'}
                        </Button>
                        <p className="text-xs text-center text-muted-foreground">
                            El administrador debe aprobar tu cuenta antes de que puedas ingresar.
                        </p>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
